import Box from "@mui/material/Box";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Render one of our markdown documents as a document, not as source.
 *
 * 🚨 remarkGfm IS NOT OPTIONAL. Plain react-markdown implements CommonMark,
 * which has no tables — so every pipe-table in the legal set was rendering as
 * literal `| --- | --- |` text on a public page. Seven of the ten documents
 * carry tables. Strikethrough, task lists and bare autolinks are GFM too.
 *
 * 🔑 One component so a second page cannot render markdown a different way.
 * The legal viewer and the namespace guide read the same styles; a fix to
 * either lands in both.
 */
export function MarkdownDoc({ md }: { md: string }) {
  return (
    <Box
      sx={{
        "& h1": { fontSize: { xs: 26, md: 32 }, fontWeight: 700, mb: 2, mt: 0 },
        "& h2": { fontSize: 21, fontWeight: 600, mt: 4, mb: 1 },
        "& h3": { fontSize: 17, fontWeight: 600, mt: 3, mb: 1 },
        "& p": { lineHeight: 1.7, mb: 2 },
        "& ul, & ol": { pl: 3, mb: 2 },
        "& li": { lineHeight: 1.7, mb: 0.5 },
        // Wide tables scroll inside their own box rather than pushing the page
        // sideways on a phone.
        "& table": {
          borderCollapse: "collapse", width: "100%", mb: 3,
          display: "block", overflowX: "auto",
        },
        "& th, & td": {
          border: "1px solid", borderColor: "divider",
          p: 1, textAlign: "left", fontSize: 14, verticalAlign: "top",
        },
        "& th": { bgcolor: "action.hover", fontWeight: 600 },
        "& code": {
          bgcolor: "action.hover", px: 0.6, py: 0.1,
          borderRadius: 0.5, fontSize: 13, fontFamily: "ui-monospace, monospace",
        },
        "& pre": {
          bgcolor: "action.hover", p: 2, borderRadius: 1,
          overflowX: "auto", fontSize: 13, mb: 2,
        },
        "& pre code": { bgcolor: "transparent", p: 0 },
        "& blockquote": {
          borderLeft: "3px solid", borderColor: "divider",
          pl: 2, ml: 0, color: "text.secondary",
        },
        "& hr": { border: 0, borderTop: "1px solid", borderColor: "divider", my: 4 },
        "& a": { color: "primary.main" },
        "& img": { maxWidth: "100%" },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
    </Box>
  );
}
